import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { adapter } from "@/services/db";
import { useAuth } from "@/context/AuthContext";
import { isSensitive, SUBCOLLECTIONS } from "@/lib/collections";
import { FieldRow } from "./FieldRow";
import { AddField } from "./AddField";
import { ConfirmTyped } from "./ConfirmTyped";
import { logEdit } from "./audit";

export const canSuperEdit = (user) => user?.role === "admin";
const errMsg = (e) => (e?.code === "permission-denied" ? "Permission denied by Firestore rules — publish the super-admin rule block." : e?.message || "Write failed");

// Live, full-fidelity view + editor for one Firestore document (any path).
export function DocumentEditor({ path, id, onDeleted }) {
  const { user } = useAuth();
  const [data, setData] = useState(undefined);
  const [q, setQ] = useState("");
  const [pending, setPending] = useState(null);
  useEffect(() => { setData(undefined); return adapter.subscribeDoc(path, id, setData); }, [path, id]);

  const canEdit = canSuperEdit(user);
  const sensitive = isSensitive(path, id);
  const fields = useMemo(() => (data ? Object.keys(data).filter((k) => k !== "id").sort() : []), [data]);
  const shown = fields.filter((k) => !q || k.toLowerCase().includes(q.toLowerCase()));
  const parent = path.split("/")[0];
  const subs = path.includes("/") ? [] : (SUBCOLLECTIONS[parent] || []);

  const guarded = (title, run) => (sensitive
    ? new Promise((resolve) => setPending({ title, description: `${path}/${id} is a sensitive document.`, run: async () => resolve(await run()) }))
    : run());

  const write = (field, next, action) => guarded(`${action} "${field}"`, async () => {
    const before = data?.[field];
    try {
      await adapter.patchField(path, id, field, next);
      await logEdit(user, { path, id, action, field, before, after: next, sensitive });
      toast.success(`${field} ${action === "delete_field" ? "deleted" : "saved"} to Firestore`);
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  });

  const deleteDoc = () => setPending({
    title: "Delete document", word: "DELETE", description: `This permanently deletes ${path}/${id} from Firestore.`,
    run: async () => {
      try { await adapter.remove(path, id); await logEdit(user, { path, id, action: "delete_document", before: data, sensitive }); toast.success("Document deleted"); onDeleted?.(); }
      catch (e) { toast.error(errMsg(e)); }
    },
  });

  if (data === undefined) return <p className="text-xs text-muted-foreground py-6 text-center" data-testid="doc-loading">Loading live document…</p>;
  if (data === null) return <p className="text-xs text-muted-foreground py-6 text-center" data-testid="doc-missing">Document {path}/{id} does not exist (or is not readable).</p>;

  return (
    <div data-testid="document-editor">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {sensitive && <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 text-red-700 px-2 py-0.5 text-[11px] font-medium" data-testid="doc-sensitive-badge"><Icons.Lock className="h-3 w-3" /> Sensitive — typed confirmation</span>}
        {!canEdit && <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground" data-testid="doc-readonly-badge"><Icons.Eye className="h-3 w-3" /> Read-only (admin only edits)</span>}
        <span className="text-[11px] font-mono text-muted-foreground" data-testid="doc-field-count">{fields.length} fields</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[160px]">
          <Icons.Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter fields…" className="h-8 pl-8 text-sm" data-testid="doc-field-search" />
        </div>
        {canEdit && <Button size="sm" variant="outline" className="h-8 gap-1 text-red-600 border-red-200" onClick={deleteDoc} data-testid="doc-delete-btn"><Icons.Trash2 className="h-3.5 w-3.5" /> Delete doc</Button>}
      </div>
      {canEdit && <div className="mb-3"><AddField existing={fields} onAdd={(k, v) => write(k, v, "add_field")} /></div>}
      <div className="rounded-lg border border-border">
        {shown.map((k) => (
          <FieldRow key={k} name={k} value={data[k]} canEdit={canEdit}
            onSave={(f, v) => write(f, v, "update_field")}
            onDelete={(f) => write(f, undefined, "delete_field")} />
        ))}
        {shown.length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">No fields.</p>}
      </div>
      {subs.length > 0 && (
        <div className="mt-4" data-testid="doc-subcollections">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Subcollections</p>
          <div className="flex flex-wrap gap-2">
            {subs.flatMap((s) => s.docs.map((d) => (
              <Link key={`${s.name}/${d}`} to={`/data?path=${encodeURIComponent(`${path}/${id}/${s.name}`)}&doc=${d}`} className="text-xs font-mono rounded-md border border-border px-2 py-1 hover:bg-accent" data-testid={`sub-link-${s.name}-${d}`}>
                {s.name}/{d}
              </Link>
            )))}
          </div>
        </div>
      )}
      <ConfirmTyped pending={pending} onCancel={() => setPending(null)} />
    </div>
  );
}
